import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControl,
    FormHelperText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import CustomTextField from 'common/components/CustomTextField';
import RoundedSelect from 'common/components/RoundedSelect';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import { isValidHttpUrl } from 'common/validators/validators';
import { getSettingsDirty } from 'redux/slices/profileSlice';
import ItemSettings from '../settings/components/ItemSettings';
import useStylesSettings from '../settings/styles/useStylesSettings.styles';
import CalendarNotificationReminders from './CalendarNotificationReminders';

const Notification = (props) => {
    const { notifications, saving, hasChanges, onChange, handleSubmit, calendarVersion } = props;
    const { t } = useTranslation();
    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesSettings();
    const classes = { ...classes1, ...classes2 };
    const [selectedNotification, setSelectedNotification] = useState();
    const settingsDirty = useSelector(getSettingsDirty);

    const isAuthorize = useAuthLicenseAddOns();
    const iCall = isAuthorize(['oneToOne.iCall']);
    const iCalendar = isAuthorize(['oneToOne.iCalendar']);
    const leads = isAuthorize(['leads']);
    const iChat = isAuthorize(['oneToOne.iChat']);

    const canEditNotification = useCallback(
        (notificationKey) => {
            if (
                notificationKey === SettingsKey.DIRECT_INCOMING_CALL ||
                notificationKey === SettingsKey.ORGANIZATION_INCOMING_CALL ||
                notificationKey === SettingsKey.ICALL_QUEUE_INCOMING ||
                notificationKey === SettingsKey.ICALL_QUEUE_OUTGOING ||
                notificationKey === SettingsKey.CONTENT_CREATION
            ) {
                return iCall;
            }
            if (notificationKey === SettingsKey.CALENDAR_REMINDER) {
                return iCall || iCalendar;
            }
            if (notificationKey === SettingsKey.LOST_OPPORTUNITY_CREATION) {
                return leads;
            }
            if (notificationKey === SettingsKey.INCOMING_CHAT_REQUEST) {
                return iChat;
            }
        },
        [iCall, iCalendar, leads, iChat],
    );

    const notificationsSettings = useCallback(() => {
        return Object.keys(notifications).map((notification) => {
            const notificationName = notification.replace(/_/g, '');
            return {
                name: notificationName,
                notificationKey: notification,
                canEdit: canEditNotification(notification),
            };
        });
    }, [notifications, canEditNotification]);

    useEffect(() => {
        if (!selectedNotification) {
            const defaultNotification = notificationsSettings().find((setting) => setting.canEdit);
            setSelectedNotification(defaultNotification?.notificationKey);
        }
    }, [selectedNotification, notificationsSettings]);

    const renderNotificationSettings = () => {
        return notificationsSettings().map((setting) => (
            <MenuItem value={setting.notificationKey} key={setting.notificationKey} disabled={!setting.canEdit}>
                {t(`profile.settings.oneToOneSettings.${setting.name}`)}
            </MenuItem>
        ));
    };

    const validBaseUrl = (baseUrl) => {
        return isValidHttpUrl(baseUrl) || baseUrl === '' || !baseUrl;
    };

    const handleChangeNotification = (notificationKey, settingName, value) => {
        const typeOfNotification =
            notificationKey === SettingsKey.INCOMING_CHAT_REQUEST
                ? SettingsKey.ICHAT_NOTIFICATIONS
                : SettingsKey.ICALL_NOTIFICATIONS;
        onChange(typeOfNotification, notificationKey, settingName, value);
    };

    const handleSetReminders = (values) => {
        handleChangeNotification(selectedNotification, SettingsKey.CALENDAR_REMINDER_NOTIFICATION, values);
    };

    const isCalendarReminderVisible = useCallback(() => {
        return (
            selectedNotification === SettingsKey.CALENDAR_REMINDER &&
            (notifications[selectedNotification]?.[SettingsKey.ENABLE_SYSTEM_TRAY_NOTIFICATION] ||
                notifications[selectedNotification]?.[SettingsKey.ENABLE_BROWSER_PUSH_NOTIFICATION] ||
                notifications[selectedNotification]?.[SettingsKey.ENABLE_BROWSER_SOUND_NOTIFICATION])
        );
    }, [selectedNotification, notifications]);

    return (
        <Accordion className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ArrowRightIcon className={classes.accordionIcon} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Typography variant="subtitle1" fontWeight={500}>
                    {t('profile.settings.notifications')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.eventSection}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mb={2}>
                    <RoundedSelect
                        value={selectedNotification || ''}
                        fullWidth
                        onChange={(e) => setSelectedNotification(e.target.value)}
                        iconStyle={{ fontSize: '13px', padding: '0 12px', color: 'darkslategrey' }}
                    >
                        {renderNotificationSettings()}
                    </RoundedSelect>
                    {settingsDirty && hasChanges && !saving && (
                        <Button
                            variant="contained"
                            className={cx(classes.redColor, classes.ml1)}
                            size="small"
                            onClick={handleSubmit}
                        >
                            {t('Save changes')}
                        </Button>
                    )}
                </Box>
                <ItemSettings
                    label={t('profile.settings.oneToOneSettings.enableSystemTrayNotification')}
                    onChange={(e, settingName) =>
                        handleChangeNotification(selectedNotification, settingName, e.target.checked)
                    }
                    name={SettingsKey.ENABLE_SYSTEM_TRAY_NOTIFICATION}
                    keySettings={SettingsKey.ENABLE_SYSTEM_TRAY_NOTIFICATION}
                    value={notifications[selectedNotification]?.[SettingsKey.ENABLE_SYSTEM_TRAY_NOTIFICATION]}
                    paddingBottom
                />

                <ItemSettings
                    label={t('profile.settings.oneToOneSettings.enableBrowserPushNotification')}
                    onChange={(e, settingName) =>
                        handleChangeNotification(selectedNotification, settingName, e.target.checked)
                    }
                    name={SettingsKey.ENABLE_BROWSER_PUSH_NOTIFICATION}
                    keySettings={SettingsKey.ENABLE_BROWSER_PUSH_NOTIFICATION}
                    value={notifications[selectedNotification]?.[SettingsKey.ENABLE_BROWSER_PUSH_NOTIFICATION]}
                    paddingBottom
                />

                <ItemSettings
                    label={t('profile.settings.oneToOneSettings.enableBrowserSoundNotification')}
                    onChange={(e, settingName) =>
                        handleChangeNotification(selectedNotification, settingName, e.target.checked)
                    }
                    name={SettingsKey.ENABLE_BROWSER_SOUND_NOTIFICATION}
                    keySettings={SettingsKey.ENABLE_BROWSER_SOUND_NOTIFICATION}
                    value={notifications[selectedNotification]?.[SettingsKey.ENABLE_BROWSER_SOUND_NOTIFICATION]}
                    paddingBottom
                />

                {notifications[selectedNotification]?.[SettingsKey.ENABLE_BROWSER_SOUND_NOTIFICATION] && (
                    <Stack direction="column">
                        <Typography className={classes.text}>
                            {t('profile.settings.oneToOneSettings.urlBrowserSoundNotification')}
                        </Typography>

                        <Box style={{ width: '60%' }}>
                            <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                <FormControl>
                                    <CustomTextField
                                        value={
                                            notifications[selectedNotification]?.[
                                                SettingsKey.URL_BROWSER_SOUND_NOTIFICATION
                                            ]
                                        }
                                        onChange={(e) =>
                                            handleChangeNotification(
                                                selectedNotification,
                                                SettingsKey.URL_BROWSER_SOUND_NOTIFICATION,
                                                e.target.value,
                                            )
                                        }
                                        style={{ width: '100%', marginTop: '10px' }}
                                        variant="outlined"
                                        size="small"
                                        color={
                                            validBaseUrl(
                                                notifications[selectedNotification]?.[
                                                    SettingsKey.URL_BROWSER_SOUND_NOTIFICATION
                                                ],
                                            )
                                                ? 'primary'
                                                : 'error'
                                        }
                                        placeholder={t('profile.settings.seoSettings.baseUrl')}
                                    />
                                    <FormHelperText
                                        error={
                                            !validBaseUrl(
                                                notifications[selectedNotification]?.[
                                                    SettingsKey.URL_BROWSER_SOUND_NOTIFICATION
                                                ],
                                            )
                                        }
                                    >
                                        {!validBaseUrl(
                                            notifications[selectedNotification]?.[
                                                SettingsKey.URL_BROWSER_SOUND_NOTIFICATION
                                            ],
                                        ) && t('profile.settings.urlValidationError')}
                                    </FormHelperText>
                                </FormControl>
                            </Box>
                        </Box>
                    </Stack>
                )}

                {calendarVersion === 3 && isCalendarReminderVisible() && (
                    <CalendarNotificationReminders
                        reminders={
                            notifications[selectedNotification]?.[SettingsKey.CALENDAR_REMINDER_NOTIFICATION] || []
                        }
                        onChange={handleSetReminders}
                    />
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default Notification;
